import React, { useState, useEffect, useRef } from "react";
import { Subject, debounce, filter, switchMap, timer } from "rxjs";

import ClearIcon from "../../../assets/svg/clear.svg";
import SearchIcon from "../../../assets/svg/search.svg";
import TextBoxLoadingIcon from "../../../assets/svg/textBoxLoading.svg";

type IconType = React.ComponentType<React.SVGProps<SVGElement>> | string;

interface TextBoxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "children"> {
  leftIcon?: IconType;
  rightIcon?: IconType;
  className?: string;
  isLoading?: boolean;
  defaultText?: string;
  inputClassName?: string;
  leftIconClassName?: string;
  rightIconClassName?: string;
  clearIcon?: "LEFT" | "RIGHT" | "NONE";
}

export const TextBox: React.FC<TextBoxProps> = ({
  leftIcon,
  rightIcon,
  rightIconClassName,
  isLoading = false,
  clearIcon = "NONE",
  leftIconClassName,
  className,
  inputClassName,
  placeholder,
  onFocus,
  onBlur,
  onChange,
  ...rest
}) => {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputPlaceholder, setInputPlaceholder] = useState(placeholder);

  const clear = () => {
    setInput("");
    triggerChangeEvent("");
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setInputPlaceholder("");
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setInputPlaceholder(placeholder);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleLeftIconClick = () => {
    if (clearIcon === "LEFT") {
      clear();
      return;
    }
  };

  const handleRightIconClick = () => {
    if (clearIcon === "RIGHT") {
      clear();
      return;
    }
  };

  const triggerChangeEvent = (value: string) => {
    if (inputRef.current) {
      const event = new Event("input", { bubbles: true });
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
      if (nativeInputValueSetter) {
        nativeInputValueSetter.call(inputRef.current, value);
      }
      inputRef.current.dispatchEvent(event);
      if (onChange) {
        onChange({ target: inputRef.current } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const renderIcon = (icon: IconType, iconClassName: string, placement: "LEFT" | "RIGHT") => {
    if (typeof icon === "string") {
      const onClickHandler = placement === "LEFT" ? handleLeftIconClick : handleRightIconClick;

      return <img src={icon} alt="icon" className={`inline-block ${iconClassName}`} onClick={onClickHandler} />;
    } else {
      const onClickHandler = placement === "LEFT" ? handleLeftIconClick : handleRightIconClick;
      return React.createElement(icon, { className: `inline-block ${iconClassName}`, onClick: onClickHandler });
    }
  };

  // Explicitly exclude children from rest - don't remove this. Hiding lint error.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, ...inputProps } = rest;

  const calculatedRightIcon = isLoading ? TextBoxLoadingIcon : rightIcon;
  const updatedRightIconClassName = isLoading ? `animate-spin -mt-1 ${rightIconClassName}` : rightIconClassName;

  return (
    <div
      className={`flex items-center justify-items-start bg-neutral-20 border border-neutral-200 rounded-md px-3 py-2 focus-within:ring focus-within:border-primary-blue-560 hover:bg-white h-10 ${className}`}
      onClick={(e) => {
        // Focus the input element when clicking anywhere on the TextBox
        const input = e.currentTarget.querySelector("input");

        if (input) {
          input.focus();
        }
      }}
    >
      {leftIcon && <span className="mr-2">{renderIcon(leftIcon, leftIconClassName ?? "", "LEFT")}</span>}
      <input
        type="text"
        ref={inputRef}
        className={`bg-neutral-20 my-2 flex-grow outline-none text-neutral-600 placeholder-neutral-400 bg-transparent placeholder-truncate ${inputClassName}`}
        {...inputProps}
        value={input}
        placeholder={inputPlaceholder}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onChange={(e) => {
          setInput(e.target.value);
          onChange && onChange(e);
        }}
      />
      {calculatedRightIcon && (
        <span className="ml-2">{renderIcon(calculatedRightIcon, updatedRightIconClassName ?? "", "RIGHT")}</span>
      )}
    </div>
  );
};

interface SearchProps extends TextBoxProps {
  throttleMs?: number;
  minTextLength?: number;

  onSearchAsync?: (term: string) => Promise<unknown>;
}

export const SearchTextBox = ({
  minTextLength = 1,
  throttleMs = 300,
  isLoading = false,
  placeholder = "Search...",
  rightIconClassName = "cursor-pointer",
  onSearchAsync,
  ...props
}: SearchProps) => {
  const searchBoxChangeSubject = useRef(new Subject<string>());

  useEffect(() => {
    const sub = searchBoxChangeSubject.current
      .pipe(filter((searchText) => searchText.length === 0 || searchText.length > minTextLength))
      .pipe(debounce((searchText) => timer(searchText.length === 0 ? 0 : throttleMs)))
      .pipe(
        switchMap(async (searchText) => {
          if (onSearchAsync) {
            await onSearchAsync(searchText);
          }
        })
      )
      .subscribe(() => {});

    return () => {
      sub.unsubscribe();
    };
  }, [minTextLength, throttleMs, onSearchAsync]);

  const onTextChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;

    searchBoxChangeSubject.current.next(text);

    if (props.onChange) {
      props.onChange(e);
    }
  };

  return (
    <TextBox
      {...props}
      clearIcon="RIGHT"
      isLoading={isLoading}
      leftIcon={SearchIcon}
      rightIcon={ClearIcon}
      placeholder={placeholder}
      rightIconClassName={rightIconClassName}
      onChange={(e) => onTextChanged(e)}
    />
  );
};

export default TextBox;
